import * as React from 'react';
import { CHART_INK } from './chart-utils';

export type TooltipRow = { label: string; value: string; color?: string };

export type ChartTooltipState = {
  x: number;
  y: number;
  title: string;
  rows: TooltipRow[];
};

type Props = {
  state: ChartTooltipState | null;
  /** Width of the containing element, used to flip near the right edge. */
  containerWidth: number;
};

const TOOLTIP_WIDTH = 180;

/**
 * Small HTML tooltip positioned inside a `relative` chart container.
 * Flips to the left of the cursor when it would overflow the right edge.
 */
export function ChartTooltip({ state, containerWidth }: Props) {
  if (!state) return null;
  const flip = state.x + TOOLTIP_WIDTH + 16 > containerWidth;
  const left = flip ? state.x - TOOLTIP_WIDTH - 12 : state.x + 12;
  return (
    <div
      role="tooltip"
      data-testid="chart-tooltip"
      className="pointer-events-none absolute z-10 rounded-md border border-gray-200 bg-white px-3 py-2 shadow-md"
      style={{ left, top: Math.max(0, state.y - 8), width: TOOLTIP_WIDTH, fontSize: 12 }}
    >
      <div className="mb-1 font-medium" style={{ color: CHART_INK.primary }}>
        {state.title}
      </div>
      {state.rows.map((row) => (
        <div key={row.label} className="flex items-center justify-between gap-2 leading-5">
          <span className="flex min-w-0 items-center gap-1.5" style={{ color: CHART_INK.secondary }}>
            {row.color ? (
              <span
                aria-hidden="true"
                className="inline-block h-2 w-2 shrink-0 rounded-full"
                style={{ backgroundColor: row.color }}
              />
            ) : null}
            <span className="truncate">{row.label}</span>
          </span>
          <span className="shrink-0 font-medium tabular-nums" style={{ color: CHART_INK.primary }}>
            {row.value}
          </span>
        </div>
      ))}
    </div>
  );
}
